import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

/**
 * Fit-to-slot wrapper (shared editor + public Grid). Measures its child's
 * NATURAL size and applies a uniform `scale()` so the content always CONTAINS
 * within the element's box — the exact same "resize to the slot" rule the text
 * modules (AutoFitText) already follow. Icon/label/padding params authored in
 * the inspector become RELATIVE proportions; resizing the slot rescales the
 * whole block. `boxW`/`boxH` are screen px (already include camera zoom), so the
 * block naturally grows/shrinks with zoom without ever overflowing.
 */
export function FitToBox({
  boxW, boxH, alignX = "center", alignY = "center", children,
}: {
  boxW: number; boxH: number;
  alignX?: "flex-start" | "center" | "flex-end";
  alignY?: "flex-start" | "center" | "flex-end";
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [k, setK] = useState(1);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const nw = el.offsetWidth, nh = el.offsetHeight;
    if (nw > 0 && nh > 0) {
      const next = Math.min(boxW / nw, boxH / nh);
      if (isFinite(next) && next > 0 && Math.abs(next - k) > 0.002) setK(next);
    }
  });
  const originX = alignX === "flex-start" ? "left" : alignX === "flex-end" ? "right" : "center";
  const originY = alignY === "flex-start" ? "top" : alignY === "flex-end" ? "bottom" : "center";
  return (
    <div style={{ width: boxW, height: boxH, overflow: "hidden", display: "flex", justifyContent: alignX, alignItems: alignY, boxSizing: "border-box" }}>
      <div ref={ref} style={{ transform: `scale(${k})`, transformOrigin: `${originX} ${originY}`, flex: "0 0 auto" }}>
        {children}
      </div>
    </div>
  );
}
